import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Statistic
} from 'antd';
import { ExportOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { investmentsApi } from './investmentsApi';
import {
  DISPOSAL_STATUSES, findDisposalFormErrors, friendlyErrorFor,
  calculateGainLossPreview, formatRupee
} from './investmentsHelpers';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Disposal modal (Stage 6B.9E).
//
// Operator enters disposal date + value + receiving account → live
// gain/loss preview → Post (Approval-MFA L1).
// ─────────────────────────────────────────────────────────────────

function LedgerSelect({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try { return await investmentsApi.ledgerLookup(q || '', 100); }
    finally { setLoading(false); }
  }, []);
  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* */ }
  }, [options.length, fetcher]);
  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* */ }
    }, 200);
  }, [fetcher]);
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: <span>
          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
          {o.accountName}
        </span>
      }))}
    />
  );
}

export default function InvestmentDisposalModal({
  open, investment, onClose, onDisposed, runWithApproval
}) {
  const [form] = Form.useForm();
  const [disposalValue, setDisposalValue] = useState(0);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      disposalDate: dayjs(),
      disposalValue: investment ? Number(investment.principalAmount) || 0 : 0,
      status: 'sold',
      receivingAccountId: null,
      reason: ''
    });
    setDisposalValue(investment ? Number(investment.principalAmount) || 0 : 0);
  }, [open, form, investment]);

  const preview = useMemo(() => {
    if (!investment) return null;
    return calculateGainLossPreview({
      principalAmount: investment.principalAmount,
      disposalValue
    });
  }, [investment, disposalValue]);

  const runDispose = useCallback(async () => {
    if (!investment) return;
    try {
      const values = await form.validateFields();
      const body = {
        disposalDate:       values.disposalDate.format('YYYY-MM-DD'),
        disposalValue:      Number(values.disposalValue) || 0,
        status:             values.status || 'sold',
        receivingAccountId: values.receivingAccountId || null,
        reason:             values.reason || null
      };
      const errs = findDisposalFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        const gainLossText = preview && preview.gainLossOnDisposal > 0
          ? `gain of ₹ ${formatRupee(preview.gainLossOnDisposal)}`
          : preview && preview.gainLossOnDisposal < 0
            ? `loss of ₹ ${formatRupee(Math.abs(preview.gainLossOnDisposal))}`
            : 'no gain / loss';
        Modal.confirm({
          title: `Dispose ${investment.investmentCode}?`,
          icon:  <CheckCircleOutlined style={{ color: '#3f8600' }} />,
          content: `This will post a disposal JV through the engine (${gainLossText}). You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Dispose', okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk:    () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;
      setPosting(true);
      await runWithApproval({
        operationKey:   'accounts.investment.dispose',
        resourceType:   'investment',
        resourceId:     investment.id,
        operationLabel: 'Dispose investment',
        resourceLabel:  `${investment.investmentCode} · ${investment.investmentName}`,
        action: (approvalMfaChallengeId) =>
          investmentsApi.dispose(investment.id, body, { approvalMfaChallengeId })
      });
      message.success('Investment disposed.');
      onDisposed && onDisposed();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setPosting(false); }
  }, [investment, form, preview, runWithApproval, onDisposed]);

  if (!investment) return null;

  return (
    <Modal
      title={`Dispose ${investment.investmentCode} · ${investment.investmentName}`}
      open={open}
      onCancel={onClose}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button type="primary" danger icon={<ExportOutlined />}
            loading={posting} onClick={runDispose}>
            Post Disposal
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form"
        onValuesChange={(_c, all) => {
          if ('disposalValue' in (_c || {})) {
            setDisposalValue(Number(all.disposalValue) || 0);
          }
        }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Disposal Date" name="disposalDate"
              rules={[{ required: true, message: 'Disposal date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Status" name="status"
              rules={[{ required: true, message: 'Disposal status is required.' }]}>
              <Select options={DISPOSAL_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Disposal Value (₹)" name="disposalValue">
              <InputNumber min={0} step={1000} style={{ width: '100%' }}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Receiving Account (required if Disposal Value > 0)" name="receivingAccountId">
          <LedgerSelect placeholder="Cash / Bank / Sundry Debtor ledger" />
        </Form.Item>
        <Form.Item label="Reason / Remarks" name="reason">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="e.g. Sold via Zerodha; broker note BN-12345" />
        </Form.Item>
      </Form>

      {!investment.gainLossLedgerAccountId ? (
        <Alert type="warning" showIcon style={{ marginTop: 10 }}
          message="Gain/Loss ledger not configured on this investment"
          description="Disposal posting requires a Gain/Loss ledger whenever the disposal value differs from the principal amount. Configure it on the investment first." />
      ) : null}

      {preview ? (
        <div style={{ marginTop: 12 }}>
          <Row gutter={[10, 10]}>
            <Col xs={12} md={6}>
              <Statistic title="Principal (₹)" value={preview.principal} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Proceeds (₹)" value={preview.proceeds} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={preview.gainLossOnDisposal >= 0 ? 'Gain (₹)' : 'Loss (₹)'}
                value={Math.abs(preview.gainLossOnDisposal)} precision={2}
                valueStyle={{ color: preview.gainLossOnDisposal >= 0 ? '#3f8600' : '#cf1322' }} />
            </Col>
            <Col xs={12} md={6}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Code">{investment.investmentCode}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          <Alert type="info" showIcon style={{ marginTop: 10 }}
            message="The disposal JV will be posted through the engine"
            description={
              <span>
                {preview.proceeds > 0 ? `DR Receiving Account (${formatRupee(preview.proceeds)}) ` : ''}
                {preview.isLoss ? `DR Loss on Investment (${formatRupee(Math.abs(preview.gainLossOnDisposal))}) ` : ''}
                CR Investment Ledger ({formatRupee(preview.principal)})
                {preview.isGain ? ` CR Gain on Investment (${formatRupee(preview.gainLossOnDisposal)})` : ''}
              </span>
            } />
        </div>
      ) : null}
    </Modal>
  );
}
